"use client";

import "react-color-palette/css";

import { Button } from "@repo/ui/ui/button";
import { Separator } from "@repo/ui/ui/separator";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Hue, useColor } from "react-color-palette";

import { ICON_COLORS } from "./avatar-picker.constants";
import { useAvatarPicker } from "./avatar-picker.context";
import { HexInput } from "./hex-input";
import { IconColorsConicGradient } from "./icon-colors-conic-gradient";
import { IconColorsGrid } from "./icon-colors-grid";
import { CustomIconsGrid } from "./icons-grid";

export const Icons = () => {
  const { iconColor, setIconColor } = useAvatarPicker();

  const isCustomColor = !ICON_COLORS.includes(iconColor);
  const [localColor, setLocalColor] = useColor(
    isCustomColor ? iconColor : "#FF0000",
  );
  const [isPickingColor, setPickingColor] = useState(false);

  return (
    <>
      {/* Color bar */}
      <div className="flex shrink-0 items-center justify-center gap-2 border-b p-2">
        <AnimatePresence mode="popLayout" initial={false}>
          {isPickingColor ? (
            <motion.div
              key="hue"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 220, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="[&_.rcp-hue-cursor]:transform-[translate(-6px,0px)] overflow-visible rounded-md [&_.rcp-hue-cursor]:size-3"
            >
              <Hue
                color={localColor}
                onChange={setLocalColor}
                onChangeComplete={(c) => setIconColor(c.hex)}
              />
            </motion.div>
          ) : (
            <motion.div
              key="colors"
              initial={{ width: 0 }}
              animate={{ width: "auto" }}
              exit={{ width: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-1 shrink-0 items-center justify-around gap-0 overflow-hidden"
            >
              {ICON_COLORS.map((color) => (
                <Button
                  key={color}
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setIconColor(color)}
                  className="group flex items-center justify-center focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <span
                    className="size-4 rounded transition-transform hover:scale-115 group-focus-visible:scale-115"
                    style={{ background: color }}
                  />
                </Button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <Separator orientation="vertical" className="my-1" />

        {/* Toggle between hue picker and color grid */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setPickingColor((p) => !p)}
            className="justify-center align-center"
          >
            {isPickingColor ? (
              <IconColorsGrid />
            ) : (
              <IconColorsConicGradient
                color={isCustomColor ? iconColor : undefined}
              />
            )}
          </Button>

          {/* Hex input (only visible when not picking from hue slider) */}
          <AnimatePresence initial={false}>
            {!isPickingColor && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "auto", opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="shrink grow-0 overflow-hidden"
              >
                <HexInput />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <CustomIconsGrid />
    </>
  );
};
